import FavoriteButton from '@/ui/Layout/Favorites/FavoriteButton';
import type { Session } from '@/types/session.types';
import HelpButton from '@/ui/Layout/Header/HelpButton';
import { hubThemeLight } from '@/ui/theme';
import { CustomThemeProvider } from '@/ui/theme/ThemeContext';
import { hubAmplifySignOut } from '@/utils/auth/clearMfaSessionSatisfaction';
import { setCallback } from '@/utils/auth/callbacks';
import { generateColor } from '@/utils/colors';
import useSession from '@/utils/hooks/useSession';
import useHubTranslation from '@/utils/i18n/useHubTranslation';
import { stopImpersonatingUser } from '@/utils/impersonation';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useRouter } from 'next/router';
import { FC, MouseEvent, useState } from 'react';

export const HEADER_HEIGHT = 64;

const HeaderBar: FC = () => {
  const router = useRouter();
  const { translateHubUi } = useHubTranslation();
  const { user, isImpersonating }: Session = useSession() as Session;

  const clearImpersonatedUser = () => {
    stopImpersonatingUser();

    router.reload();
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const closeMenu = (): void => {
    setAnchorEl(null);
  };

  const editAccount = (): void => {
    router.push(`/personal-settings/profile`);
  };

  const changePasswordRedirect = (): void => {
    router.push('/personal-settings/change-password');
  };

  const logoutClick = async (): Promise<void> => {
    await hubAmplifySignOut({
      reason: 'hub-header',
      userEmail: user?.email,
    });
    stopImpersonatingUser();
    if (router.asPath) {
      setCallback(router.asPath);
    }
    router.push(`/login`);
  };

  const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(e.currentTarget);
  };

  return (
    <CustomThemeProvider initialSettings={{ themeOverride: hubThemeLight }}>
      <AppBar
        position='sticky'
        elevation={0}
        sx={{
          height: HEADER_HEIGHT,
          display: 'flex',
          flexDirection: 'row',
          zIndex: 'drawer',
          bgcolor: 'rgba(255,255,255,0.5)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
        color='default'
      >
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box>
            <FavoriteButton />
          </Box>
          <Box>
            <HelpButton />
          </Box>
          {isImpersonating && (
            <Typography
              variant='caption'
              sx={(theme) => ({
                marginLeft: theme.spacing(2),
                marginRight: theme.spacing(2),
                whiteSpace: 'nowrap',
                ...theme.typography.caption,
              })}
            >
              {translateHubUi('nav.header.impersonating', 'Impersonating')}
            </Typography>
          )}
          <Box
            sx={(theme) => ({ height: '100%', marginRight: theme.spacing(4) })}
          >
            <Button
              sx={(theme) => ({
                display: 'inline-flex',
                width: 'auto',
                alignItems: 'center',
                paddingRight: 0,
                height: '100%',
                backgroundColor: 'transparent',
                position: 'relative',
                '&:hover, &:focus, &:active': {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                },
                '& > *': {
                  margin: theme.spacing(1),
                },
              })}
              onClick={handleOpenMenu}
              color='inherit'
              aria-label={
                user?.firstName && user?.lastName
                  ? `Open account menu for ${user.firstName} ${user.lastName}`
                  : translateHubUi(
                      'nav.header.accountMenu.open',
                      'Open account menu'
                    )
              }
              title={
                user?.firstName && user?.lastName
                  ? `Open account menu for ${user.firstName} ${user.lastName}`
                  : translateHubUi(
                      'nav.header.accountMenu.open',
                      'Open account menu'
                    )
              }
            >
              <span aria-hidden>
                <Avatar
                  style={{ backgroundColor: generateColor(user?.lastName) }}
                  sx={{
                    transition: 'box-shadow 0.2s',
                    boxShadow: (theme) =>
                      anchorEl
                        ? `0 0 0 4px #fff, 0 0 0 6px ${theme.palette.primary.main}`
                        : 'none',
                    '&:hover, &:focus': {
                      boxShadow: (theme) =>
                        `0 0 0 4px #fff, 0 0 0 6px ${theme.palette.primary.main}`,
                    },
                  }}
                >
                  {`${user?.firstName?.[0]}${user?.lastName?.[0]}`}
                </Avatar>
              </span>
            </Button>

            <Menu
              id='account-menu'
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={closeMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: (theme) => ({
                    ml: -5,
                    boxShadow: `0 0 2px 0 ${theme.palette.grey[500]}40, -20px 20px 40px -4px ${theme.palette.grey[500]}40`,
                    bgcolor: theme.palette.background.paper,
                  }),
                },
              }}
            >
              <>
                <Box
                  sx={(theme) => ({
                    px: theme.spacing(2),
                    py: theme.spacing(1.5),
                  })}
                >
                  <Typography
                    variant='subtitle1'
                    sx={(theme) => ({ ...theme.typography.subtitle1 })}
                  >
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={(theme) => ({ ...theme.typography.body2 })}
                  >
                    {user?.email}
                  </Typography>
                </Box>

                <Divider sx={{ mt: 1, mb: 1 }} />
              </>

              {isImpersonating && (
                <MenuItem
                  onClick={() => clearImpersonatedUser()}
                  sx={{ px: 0, py: 0 }}
                >
                  <Box
                    sx={(theme) => ({
                      px: theme.spacing(2),
                      py: theme.spacing(1.5),
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    })}
                  >
                    {translateHubUi(
                      'nav.header.accountMenu.stopImpersonating',
                      'Stop Impersonating User'
                    )}
                  </Box>
                </MenuItem>
              )}

              <MenuItem onClick={editAccount} sx={{ px: 0, py: 0 }}>
                <Box
                  sx={(theme) => ({
                    px: theme.spacing(2),
                    py: theme.spacing(1.5),
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  })}
                >
                  {translateHubUi(
                    'nav.header.accountMenu.myProfile',
                    'My profile'
                  )}
                </Box>
              </MenuItem>

              <MenuItem onClick={changePasswordRedirect} sx={{ px: 0, py: 0 }}>
                <Box
                  sx={(theme) => ({
                    px: theme.spacing(2),
                    py: theme.spacing(1.5),
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  })}
                >
                  {translateHubUi(
                    'nav.header.accountMenu.changePassword',
                    'Change password'
                  )}
                </Box>
              </MenuItem>

              <Divider />

              <MenuItem onClick={logoutClick} sx={{ px: 0, py: 0 }}>
                <Box
                  sx={(theme) => ({
                    px: theme.spacing(2),
                    py: theme.spacing(1.5),
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  })}
                >
                  <ListItemText>
                    {translateHubUi('nav.header.accountMenu.logOut', 'Log out')}
                  </ListItemText>
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </AppBar>
    </CustomThemeProvider>
  );
};

export default HeaderBar;
